import { useCallback, useState } from "react"

import type { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useMultiFileUpload } from "@/hooks/useMultiFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UpdateTransferDocumentsParams {
  transferId: string
  documentsToUpload?: File[]
  documentsToDelete?: string[]
}

export interface UseUpdateTransferDocumentsReturn {
  updateDocuments: (params: UpdateTransferDocumentsParams) => Promise<boolean>
  documentUploadStatuses: Record<string, UploadRowStatus>
  isUpdatingDocuments: boolean
}

export const useUpdateTransferDocuments = (): UseUpdateTransferDocumentsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { removeSingleFile } = useFilesRemove()
  const [isDeletingDocuments, setIsDeletingDocuments] = useState(false)
  const {
    statuses: documentUploadStatuses,
    isUploading: isUploadingDocuments,
    uploadFiles,
  } = useMultiFileUpload({ referenceTableName: ReferenceTableName.Transfer })

  const updateDocuments = useCallback(
    async ({
      transferId,
      documentsToUpload = [],
      documentsToDelete = [],
    }: UpdateTransferDocumentsParams): Promise<boolean> => {
      if (documentsToDelete.length) {
        setIsDeletingDocuments(true)

        for (const fileId of documentsToDelete) {
          const isRemoved = await removeSingleFile(fileId)

          if (!isRemoved) {
            notify({ type: NotificationType.Error, message: "Failed to remove a document." })
          }
        }

        setIsDeletingDocuments(false)
      }

      if (!documentsToUpload.length) return false

      const items = documentsToUpload.map((document, index) => ({
        id: String(index),
        file: document,
        fieldGroup: FieldGroupEnum.Transfer,
      }))

      const { failures } = await uploadFiles(items, {
        referenceIds: [transferId],
        totalFailureMessage: "Transfer saved, but no documents were attached.",
      })

      return failures.length === items.length
    },
    [removeSingleFile, notify, uploadFiles]
  )

  return {
    updateDocuments,
    documentUploadStatuses,
    isUpdatingDocuments: isDeletingDocuments || isUploadingDocuments,
  }
}
